import { useEffect, useState } from 'react';

import {
  Button,
  Select,
  Dialog,
  MenuItem,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Typography,
  SelectChangeEvent,
  Snackbar,
  Alert,
  styled,
} from '@mui/material';

import { SlotInfo } from 'react-big-calendar';
import { observer } from 'mobx-react-lite';

import moment from 'moment';

import {
  ReservierungsDetails,
  ReservierungsMoeglichkeit,
} from '@codefluegel/wowis-rest-typescript-client';

import DateTimePicker from '../datepicker/DateTimePicker';
import { useReservationApi } from '../../ApiClient';

const MenuItemStyled = styled(MenuItem)({
  color: '#58585A',
});

export type CalendarEvent = {
  id: number;
  title: string;
  allDay: boolean;
  start: Date;
  end: Date;
  roomId: number;
  reservationData?: ReservierungsDetails;
  roomOccupancy?: number | null | undefined;
  top_nutzer_id?: number;
};

type OccupancyObject = {
  value: number;
  label: string;
};

const AddReservationModal = observer(
  ({
    open,
    customerId,
    reservationsPossiblities,
    initialData,
    currentSlot,
    onClose,
    onRefetch,
  }: {
    open: boolean;
    customerId: number;
    reservationsPossiblities: ReservierungsMoeglichkeit[];
    initialData: CalendarEvent | undefined;
    currentSlot: SlotInfo | undefined;
    onClose: () => void;
    onRefetch: () => void;
  }): JSX.Element => {
    const reservationApi = useReservationApi();

    // UI states
    const [snackBar, setSnackBar] = useState(false);

    // Error handling
    const [errorMsg, setErrorMsg] = useState<string>('');

    // Form states;
    const [startDateState, setStartDateState] = useState<Date | undefined>();
    const [endDateState, setEndDateState] = useState<Date | undefined>();

    const [room, setRoom] = useState<string>('');

    const [occupancyState, setOccupancyState] = useState<
      string | null | undefined
    >('');
    const [availableOccupancy, setAvailableOccupancy] = useState<
      OccupancyObject[]
    >([]);

    // helper to get the reservation possibility by id
    function getReservationPossibilityById(id: string) {
      const foundResult = reservationsPossiblities.filter(
        res => res.reservierungs_moeglichkeits_id === Number(id),
      );

      return foundResult[0];
    }

    // array to map over to generate possible occupancy values
    function generateArrayOfOccupancyObjects(
      pos: ReservierungsMoeglichkeit,
    ): OccupancyObject[] {
      const min = pos?.min_belegung;
      const max = pos?.max_belegung;

      if (!min || !max) return [];

      const defaultStart = min === undefined || min === null ? 1 : min;
      const defaultEnd = max === undefined || max === null ? 10 : max;

      const myArray: OccupancyObject[] = [];

      let currentValue = defaultStart;
      while (currentValue <= defaultEnd) {
        myArray.push({
          value: currentValue,
          label: currentValue.toString(),
        });

        currentValue += 1;
      }

      return myArray;
    }

    useEffect(() => {
      // set initail state
      if (initialData) {
        setStartDateState(initialData.start);
        setEndDateState(initialData.end);
        setRoom(initialData.roomId.toString());
        setOccupancyState(initialData.roomOccupancy?.toString());
      } else if (currentSlot && !startDateState && !endDateState) {
        setStartDateState(currentSlot.start);
        setEndDateState(currentSlot.end);
      }

      // if there are more than one reservation possibility, we need to set the selected one
      const initialPossibility = getReservationPossibilityById(
        initialData?.roomId.toString() || '',
      );

      // if there is only one reservation possibility, we can set the occupancy state
      if (initialPossibility) {
        setAvailableOccupancy(
          generateArrayOfOccupancyObjects(initialPossibility),
        );
      }

      // if there is no clickedCalenderEvent, we need to set the first reservation possibility
      if (room) {
        const selectedPossibility = getReservationPossibilityById(room);
        setAvailableOccupancy(
          generateArrayOfOccupancyObjects(selectedPossibility),
        );
      }
    }, [initialData, open, room, occupancyState, currentSlot]);

    const handleClose = () => {
      onClose();

      // reset all states
      setStartDateState(undefined);
      setEndDateState(undefined);
      setRoom('');
      setOccupancyState('');
      setAvailableOccupancy([]);
    };

    // close snackbar
    const handleSnackClose = () => {
      setSnackBar(false);
      setErrorMsg('');
    };

    // delete reservation
    const handleDelete = async () => {
      if (initialData) {
        try {
          await reservationApi.deleteReservierung(
            customerId,
            initialData.roomId,
            initialData.id,
          );
          onRefetch();
          onClose();
        } catch (error: any) {
          setErrorMsg(error.response.data);
        }
      }
    };

    // save reservation
    const saveEntry = async () => {
      const slot = {
        beginn: moment(startDateState).format(),
        ende: moment(endDateState).format(),
        belegung: Number(occupancyState),
      };

      try {
        await reservationApi.addReservierung(customerId, Number(room), slot);
        onRefetch();
        onClose();
      } catch (error: any) {
        setErrorMsg(error.response.data.title);
        setSnackBar(true);
      }
    };

    // update reservation
    const handleUpdate = async () => {
      if (initialData) {
        const slot = {
          beginn: moment(startDateState).format(),
          ende: moment(endDateState).format(),
          belegung: Number(occupancyState),
        };

        try {
          await reservationApi.updateReservierung(
            customerId,
            initialData.roomId,
            initialData.id,
            slot,
          );
          onRefetch();
          onClose();
        } catch (error: any) {
          setErrorMsg(error.response.data.title);
          setSnackBar(true);
        }
      }
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle color="secondary">
          {initialData ? 'Reservierung' : 'Neue Reservierung anlegen'}
        </DialogTitle>
        <DialogContent sx={{ width: { md: 500 } }}>
          <hr />
          <Typography color="secondary">
            Bitte die benötigten Felder ausfüllen
          </Typography>

          <FormLabel component="legend">Raum wählen</FormLabel>
          <Select
            fullWidth
            sx={{ marginTop: 2, marginBottom: 2, color: '#58585A' }}
            value={room}
            onChange={(event: SelectChangeEvent<string>) => {
              setRoom(event.target.value);
            }}
          >
            {reservationsPossiblities.map(res => (
              <MenuItemStyled
                key={res.reservierungs_moeglichkeits_id}
                value={res.reservierungs_moeglichkeits_id}
              >
                {res.text}
              </MenuItemStyled>
            ))}
          </Select>

          <FormLabel component="legend">Von</FormLabel>
          {startDateState && (
            <DateTimePicker
              onClose={date => {
                setStartDateState(date ?? undefined);
              }}
              initDate={startDateState}
            />
          )}

          <FormLabel component="legend">Bis</FormLabel>
          {endDateState && (
            <DateTimePicker
              onClose={date => {
                setEndDateState(date ?? undefined);
              }}
              initDate={endDateState}
            />
          )}

          {availableOccupancy.length > 0 && (
            <>
              <FormLabel component="legend">Belegung</FormLabel>
              <Select value={occupancyState} sx={{ color: '#58585A' }}>
                {availableOccupancy.map(occupancyItem => (
                  <MenuItemStyled
                    key={occupancyItem.value}
                    value={occupancyItem.value}
                    onClick={() => {
                      setOccupancyState(occupancyItem.label);
                    }}
                  >
                    {occupancyItem.value.toString()}
                  </MenuItemStyled>
                ))}
              </Select>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          {initialData && initialData.top_nutzer_id === customerId && (
            <>
              <Button onClick={handleDelete}>Löschen</Button>
              <Button onClick={handleUpdate}>Aktualisieren</Button>
            </>
          )}
          {currentSlot && <Button onClick={saveEntry}>Speichern</Button>}
        </DialogActions>
        <Snackbar
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          open={snackBar}
          autoHideDuration={6000}
          onClose={handleSnackClose}
          message={errorMsg}
        >
          <Alert severity="error">{errorMsg}</Alert>
        </Snackbar>
      </Dialog>
    );
  },
);

export default AddReservationModal;
